import React, { useState } from 'react';

export default function Webform({
  formType = 'general', // 'general' | 'kropsterapi' | 'tarotterapi'
  title = 'Send en besked til Ibjerget',
  subtitle = 'Vi vender tilbage til dig hurtigst muligt.'
}) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    recipient: 'all', // 'lone' | 'niels' | 'all'
    message: '',
    website_url: '' // Mandatory honeypot field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Basic Validation
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.message.trim()) {
      setErrorMessage('Udfyld venligst alle påkrævede felter (navn, telefon, email og besked).');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setErrorMessage('Indtast venligst en gyldig e-mailadresse.');
      return;
    }

    setStatus('submitting');

    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        recipient: formData.recipient,
        message: formData.message.trim(),
        form_context: formType,
        website_url: formData.website_url // Mandatory honeypot
      };

      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          recipient: 'all',
          message: '',
          website_url: ''
        });
      } else {
        const errData = await res.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(errData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Webform error:', err);
      setStatus('error');
      setErrorMessage('Kunne ikke få forbindelse til serveren. Tjek din internetforbindelse og prøv igen.');
    }
  };

  return (
    <div className="bg-white border border-[#edd5e8] rounded-3xl p-6 sm:p-10 shadow-sm">
      <div className="mb-6">
        <h3 className="font-serif text-2xl sm:text-3xl text-[#1f142e] font-semibold tracking-tight">
          {title}
        </h3>
        {subtitle && (
          <p className="mt-2 text-sm text-[#4e3568] leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>

      {status === 'success' ? (
        <div className="bg-[#faf5f9] border border-[#edd5e8] rounded-2xl p-6 sm:p-8 text-center space-y-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#d9238c] text-white flex items-center justify-center text-2xl shadow-sm">
            <i className="fa-solid fa-check" aria-hidden="true"></i>
          </div>
          <h4 className="font-serif text-xl text-[#1f142e] font-semibold">
            Mange tak for din henvendelse
          </h4>
          <p className="text-sm text-[#4e3568] max-w-md mx-auto">
            Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt. Du er også altid velkommen til at ringe direkte til os på tlf. 30 29 79 02 (Lone) eller 66 15 79 02 (Niels).
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="inline-block mt-2 text-xs font-semibold text-[#d9238c] hover:underline"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* Honeypot field - ABSOLUTELY HIDDEN */}
          <div className="sr-only absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL (leave blank)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-4 flex items-start gap-3">
              <i className="fa-solid fa-circle-exclamation text-red-500 mt-0.5" aria-hidden="true"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-[#1f142e] mb-1.5">
                Fulde Navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="F.eks. Mette Hansen"
                className="w-full px-4 py-3 rounded-xl border border-[#edd5e8] bg-[#fcf8fb] text-[#1f142e] placeholder:text-[#8c739a] focus:outline-none focus:ring-2 focus:ring-[#d9238c] focus:border-transparent text-sm transition-all"
              />
            </div>

            {/* Telefon */}
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-[#1f142e] mb-1.5">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="Tlf. nr."
                className="w-full px-4 py-3 rounded-xl border border-[#edd5e8] bg-[#fcf8fb] text-[#1f142e] placeholder:text-[#8c739a] focus:outline-none focus:ring-2 focus:ring-[#d9238c] focus:border-transparent text-sm transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-[#1f142e] mb-1.5">
                E-mailadresse <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-4 py-3 rounded-xl border border-[#edd5e8] bg-[#fcf8fb] text-[#1f142e] placeholder:text-[#8c739a] focus:outline-none focus:ring-2 focus:ring-[#d9238c] focus:border-transparent text-sm transition-all"
              />
            </div>

            {/* Modtager */}
            <div>
              <label htmlFor="recipient" className="block text-xs font-semibold uppercase tracking-wider text-[#1f142e] mb-1.5">
                Hvem henvender du dig til?
              </label>
              <select
                id="recipient"
                name="recipient"
                value={formData.recipient}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-[#edd5e8] bg-[#fcf8fb] text-[#1f142e] focus:outline-none focus:ring-2 focus:ring-[#d9238c] focus:border-transparent text-sm transition-all"
              >
                <option value="all">Begge / Ved ikke</option>
                <option value="lone">Lone Hvid (Tarotkort, Foredrag, Bøger & Kunst)</option>
                <option value="niels">Niels Boye (ALT Ansigtsterapi, Kropsterapi, Massage)</option>
              </select>
            </div>
          </div>

          {/* Besked */}
          <div>
            <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-[#1f142e] mb-1.5">
              Din besked <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="Beskriv hvad du ønsker hjælp til eller har spørgsmål om..."
              className="w-full px-4 py-3 rounded-xl border border-[#edd5e8] bg-[#fcf8fb] text-[#1f142e] placeholder:text-[#8c739a] focus:outline-none focus:ring-2 focus:ring-[#d9238c] focus:border-transparent text-sm transition-all"
            ></textarea>
          </div>

          {/* Privacy Note */}
          <p className="text-xs text-[#9b1b66] leading-relaxed">
            Dine oplysninger behandles fortroligt og anvendes udelukkende til at besvare din henvendelse.
          </p>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full py-4 px-8 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true"></i>
                <span>Sender besked...</span>
              </>
            ) : (
              <>
                <span>Send besked</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
